'use client'

import { useEffect } from 'react'
import { useMediaQuery } from '@segway/core'
import useBraintreeGooglePay, {
  GooglePaymentConfig,
} from '@segway/core/src/businessHooks/useBraintreeGooglePay'

import { useRouter } from '@/i18n/navigation'

type GooglePayWidgetProps = {
  totalPrice: number
  parsedPaymentConfig?: GooglePaymentConfig
  pageType: 'checkout'
}

const GooglePayWidget = ({ pageType, totalPrice, parsedPaymentConfig }: GooglePayWidgetProps) => {
  const router = useRouter()

  const responsive = useMediaQuery()

  const isMobile = responsive && !responsive.xl

  const { braintreeInstance, handleTotalPriceChange } = useBraintreeGooglePay(
    router,
    'braintree_google_pay',
    totalPrice,
    pageType,
    parsedPaymentConfig,
  )

  useEffect(() => {
    if (totalPrice && braintreeInstance) {
      handleTotalPriceChange(totalPrice?.toString())

      braintreeInstance.updateConfiguration('googlePay', 'transactionInfo', {
        totalPriceStatus: 'FINAL',
        totalPrice: totalPrice.toString(),
        currencyCode: parsedPaymentConfig?.currencyCode ?? 'USD',
      })
    }
  }, [totalPrice, parsedPaymentConfig, braintreeInstance, handleTotalPriceChange])

  useEffect(() => {
    const styleId = 'google-pay-styles-' + Math.random().toString(36).substr(2, 9)
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      #braintree_google_pay .braintree-dropin .braintree-placeholder {
        display: none !important;
      }
      #braintree_google_pay .braintree-dropin .braintree-upper-container::before {
        border: none !important;
        background-color: transparent;
      }
      #braintree_google_pay .braintree-dropin .braintree-upper-container .braintree-loader__container {
        border: none !important;
        display: none !important;
      }
      #braintree_google_pay .braintree-dropin .braintree-upper-container .braintree-sheet__container .braintree-googlePay {
        border: none !important;
      }
      #braintree_google_pay .braintree-dropin .braintree-upper-container .braintree-sheet__container .braintree-googlePay .braintree-sheet__header {
        display: none !important;
        width: 100% !important;
      }

      @media (min-width: 768px) and (max-width: 1199px) {
        #braintree_google_pay .braintree-dropin .braintree-upper-container .braintree-sheet__container .braintree-googlePay .braintree-sheet__content {
          min-height: 100% !important;
          padding: 0 !important;

          .gpay-button-fill {
            .gpay-button {
              border-radius: calc(infinity * 1px) !important;
              height: 44px;
              margin-top: -3.5px !important;
              width: 750px !important;
            }
          }
        }
      }

      #braintree_google_pay .braintree-dropin .braintree-upper-container .braintree-sheet__container .braintree-googlePay .braintree-sheet__content {
        min-height: 100% !important;
        padding: 0 !important;

        .gpay-button-fill {
          .gpay-button {
            border-radius: calc(infinity * 1px) !important;
            height: 44px;
            margin-top: -3.5px !important;
          }
        }
      }
    `
    document.head.appendChild(style)

    return () => {
      const styleElement = document.getElementById(styleId)
      if (styleElement) document.head.removeChild(styleElement)
    }
  }, [isMobile])

  return <div id="braintree_google_pay" />
}

export default GooglePayWidget
